import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import AgencySettingsLayout from "@/components/agency/settings/agency-settings-layout"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { agencySettingsProfilePath } from "@/routes"

/**
 * OKS-40 — Agency Settings → Profile page.
 *
 * Two fields: name + locale. `slug` admin-only (PRD §7 — same lock
 * as Brand profile: changing slug breaks invite links).
 *
 * Member-tier (Agency Member) renders read-only.
 */

export interface AgencySettingsProfileProps {
  agency: {
    id: number
    name: string
    locale: "en" | "fr"
  }
  can_edit: boolean
}

export default function AgencySettingsProfile({
  agency,
  can_edit,
}: AgencySettingsProfileProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("settings.agency.profile.title")} />
      <AgencySettingsLayout>
        <div data-testid="agency-settings-profile" className="space-y-6">
          <HeadingSmall
            title={t("settings.agency.profile.title")}
            description={t("settings.agency.profile.description")}
          />

          <Form
            method="patch"
            action={agencySettingsProfilePath()}
            options={{ preserveScroll: true }}
            className="space-y-6"
            data-testid="agency-settings-profile-form"
          >
            {({ errors, processing }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="agency-name">
                    {t("settings.agency.profile.name_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="agency-name"
                    name="agency[name]"
                    aria-required="true"
                    aria-invalid={Boolean(errors.name)}
                    defaultValue={agency.name}
                    readOnly={!can_edit}
                    aria-readonly={!can_edit}
                    data-testid="agency-settings-profile-name"
                  />
                  <InputError messages={errors.name} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="agency-locale">
                    {t("settings.agency.profile.locale_label")}
                  </Label>
                  <Select
                    items={{ fr: "Français", en: "English" }}
                    name="agency[locale]"
                    defaultValue={agency.locale}
                    disabled={!can_edit}
                  >
                    <SelectTrigger
                      id="agency-locale"
                      data-testid="agency-settings-profile-locale"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                  <InputError messages={errors.locale} />
                </div>

                {can_edit ? (
                  <Button
                    type="submit"
                    disabled={processing}
                    data-testid="agency-settings-profile-save"
                  >
                    {processing
                      ? t("settings.agency.profile.saving")
                      : t("settings.agency.profile.save")}
                  </Button>
                ) : (
                  <p
                    role="status"
                    data-testid="agency-settings-profile-readonly"
                    className="text-muted-foreground text-sm"
                  >
                    {t("settings.agency.profile.read_only_hint")}
                  </p>
                )}
              </>
            )}
          </Form>
        </div>
      </AgencySettingsLayout>
    </AppLayout>
  )
}
